"use client";

import { Fragment } from "react";
import type { LucideIcon } from "lucide-react";
import { MoreVertical } from "lucide-react";
import { Button } from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ITEM_CLASS =
  "gap-3 px-3 py-2.5 font-medium text-[#444746] [&_svg]:text-[#444746]! focus:text-[#444746]";
const DELETE_ITEM_CLASS =
  "gap-3 px-3 py-2.5 font-medium text-[#DC2626] [&_svg]:text-[#DC2626]! focus:text-[#DC2626] focus:bg-red-50";
const CONTENT_CLASS =
  "!rounded-xl !border-none p-2 min-w-[160px] !shadow-[0_0_16px_4px_rgba(0,0,0,0.08)]";

export type ConnectorRowAction = {
  testId: string;
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
  disabled?: boolean;
  destructive?: boolean;
  separatorBefore?: boolean;
};

type ConnectorRowActionsProps = {
  buttonTestId: string;
  triggerTestId: string;
  disabled?: boolean;
  actions: ConnectorRowAction[];
};

export default function ConnectorRowActions({
  buttonTestId,
  triggerTestId,
  disabled = false,
  actions,
}: ConnectorRowActionsProps) {
  if (actions.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild data-testid={triggerTestId}>
        <Button
          data-testid={buttonTestId}
          type="button"
          variant="unstyled"
          disabled={disabled}
          className="-m-2.5 inline-flex h-8 w-8 items-center justify-center rounded-md p-2.5 text-[#444746] hover:bg-[#F4F4F4]"
        >
          <MoreVertical className="h-4 w-4" />
          <span className="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={`${CONTENT_CLASS} w-48 bg-white`}>
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <Fragment key={action.testId}>
              {action.separatorBefore && <DropdownMenuSeparator />}
              <DropdownMenuItem
                className={action.destructive ? DELETE_ITEM_CLASS : ITEM_CLASS}
                data-testid={action.testId}
                variant={action.destructive ? "destructive" : "default"}
                onSelect={action.onSelect}
                disabled={action.disabled}
              >
                <Icon />
                {action.label}
              </DropdownMenuItem>
            </Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
